// client/src/components/pages/LexiStar/NotificationRules/Templates.jsx
//
// Message templates (FR-NTF-005; LS-06 §6.2): the platform defaults per key ×
// channel × language, and this organisation's overrides. Editing creates or
// changes the override (PATCH /message-templates) with a live, server-rendered
// preview of the draft. WhatsApp templates carry the provider approval status
// and category; changing a WhatsApp body sends it back to draft (it needs
// approval again before WhatsApp will send it).

import React, { useMemo, useState } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import { inputClasses, cardClasses } from '../StatusBoard/atoms';

import TemplatePreview from './TemplatePreview';
import { updateTemplate } from './notificationsApi';
import { channelName, sampleVars } from './ruleLogic';

// The shared input classes carry w-full; fixed-width fields drop it.
const fixedWidthInput = inputClasses.replace('w-full ', '');

const PROVIDER_META = {
  approved: 'bg-emerald-50 text-emerald-800',
  submitted: 'bg-sky-50 text-sky-800',
  draft: 'bg-slate-100 text-slate-700',
  rejected: 'bg-red-50 text-red-700',
  paused: 'bg-amber-50 text-amber-800',
};

const langName = (l) => (l === 'af' ? 'Afrikaans' : l === 'en' ? 'English' : l);

/** One row per (key, channel, language): the override when there is one. */
export function effectiveTemplates(rows = []) {
  const m = new Map();
  rows.forEach((t) => {
    const k = `${t.key}|${t.channel}|${t.language}`;
    const cur = m.get(k);
    if (!cur || (t.tenant_id != null && cur.tenant_id == null))
      m.set(k, { ...t, has_platform: cur ? true : t.tenant_id == null });
    else if (cur && t.tenant_id == null) m.set(k, { ...cur, has_platform: true });
  });
  return [...m.values()].sort(
    (a, b) =>
      a.key.localeCompare(b.key) ||
      a.channel.localeCompare(b.channel) ||
      a.language.localeCompare(b.language)
  );
}

function TemplateEditor({ template, onSaved, onCancel }) {
  const [body, setBody] = useState(template.body || '');
  const [buttons, setButtons] = useState(Array.isArray(template.buttons) ? template.buttons : []);
  const [active, setActive] = useState(template.active !== false);
  const [category, setCategory] = useState(template.category || 'utility');
  const [providerName, setProviderName] = useState(template.provider_template_name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const wa = template.channel === 'whatsapp';
  const badButton = buttons.some(
    (b) => !b.label.trim() || b.label.length > 20 || !/^[A-Za-z0-9_.:-]{1,64}$/.test(b.id)
  );

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await updateTemplate({
        key: template.key,
        channel: template.channel,
        language: template.language,
        body,
        buttons,
        active,
        ...(wa ? { category, provider_template_name: providerName.trim() || null } : {}),
      });
      onSaved(res?.template || res);
    } catch (err) {
      setError(err.response?.data?.error || 'The template was not saved');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-2 gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4"
      role="group"
      aria-label="Template editor"
    >
      <div className="space-y-3">
        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}
        <div>
          <label htmlFor="tpl-body" className="block text-xs font-medium text-slate-600 mb-1">
            Body (Handlebars: {'{{trip.reference}}'}, {'{{litres trip.budget_l}}'},{' '}
            {'{{time planned_start}}'}…)
          </label>
          <textarea
            id="tpl-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            className={`${inputClasses} font-mono text-xs`}
          />
        </div>
        <div role="group" aria-label="Buttons" className="space-y-2">
          <span className="block text-xs font-medium text-slate-600">
            Reply buttons (up to 3, 20 characters)
          </span>
          {buttons.map((b, i) => (
            <div key={i} className="flex gap-2">
              <input
                aria-label={`Button ${i + 1} id`}
                value={b.id}
                onChange={(e) =>
                  setButtons(buttons.map((x, j) => (j === i ? { ...x, id: e.target.value } : x)))
                }
                className={`${fixedWidthInput} w-32 font-mono text-xs`}
              />
              <input
                aria-label={`Button ${i + 1} label`}
                value={b.label}
                maxLength={20}
                onChange={(e) =>
                  setButtons(buttons.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
                }
                className={`${inputClasses} flex-1`}
              />
              <button
                type="button"
                onClick={() => setButtons(buttons.filter((_, j) => j !== i))}
                aria-label={`Remove button ${i + 1}`}
                className="p-2 text-slate-400 hover:text-red-600"
              >
                <FaTimes size={11} />
              </button>
            </div>
          ))}
          {buttons.length < 3 && (
            <button
              type="button"
              onClick={() =>
                setButtons([...buttons, { id: `opt_${buttons.length + 1}`, label: '' }])
              }
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:underline"
            >
              <FaPlus size={9} /> Add button
            </button>
          )}
        </div>
        {wa && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="tpl-category"
                className="block text-xs font-medium text-slate-600 mb-1"
              >
                WhatsApp category
              </label>
              <ThemedSelect
                id="tpl-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={inputClasses}
              >
                <option value="utility">Utility</option>
                <option value="service">Service</option>
                <option value="marketing">Marketing</option>
              </ThemedSelect>
            </div>
            <div>
              <label
                htmlFor="tpl-provider-name"
                className="block text-xs font-medium text-slate-600 mb-1"
              >
                Meta template name
              </label>
              <input
                id="tpl-provider-name"
                value={providerName}
                onChange={(e) => setProviderName(e.target.value)}
                className={inputClasses}
              />
            </div>
            {body !== (template.body || '') && (
              <p className="col-span-2 text-xs text-amber-800">
                Changing a WhatsApp body sends the template back to draft — it must be approved by
                Meta again before WhatsApp sends it (the next channel is used meanwhile).
              </p>
            )}
          </div>
        )}
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="rounded border-slate-300"
          />
          Active
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !body.trim() || badButton}
            className="px-4 py-1.5 text-sm font-semibold text-white rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-slate-400"
          >
            {saving
              ? 'Saving...'
              : template.tenant_id != null
                ? 'Save override'
                : 'Save as our override'}
          </button>
        </div>
      </div>
      <TemplatePreview
        templateKey={template.key}
        channel={template.channel}
        vars={(lang) =>
          sampleVars(
            template.key.startsWith('DRIVER_DEVIATION')
              ? 'ROUTE.DEVIATION_OPENED'
              : 'TRIP.DISPATCHED',
            lang
          )
        }
        body={body}
        buttons={buttons.filter((b) => b.label.trim())}
      />
    </div>
  );
}

export default function Templates({ templates, canManage, onChanged }) {
  const [key, setKey] = useState('');
  const [channel, setChannel] = useState('');
  const [language, setLanguage] = useState('');
  const [editing, setEditing] = useState(null);
  const rows = useMemo(() => effectiveTemplates(templates), [templates]);
  const keys = useMemo(() => [...new Set(rows.map((t) => t.key))].sort(), [rows]);
  const visible = rows.filter(
    (t) =>
      (!key || t.key === key) &&
      (!channel || t.channel === channel) &&
      (!language || t.language === language)
  );
  const rowKey = (t) => `${t.key}|${t.channel}|${t.language}`;

  return (
    <section className={cardClasses} aria-label="Message templates">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="sm:w-72">
          <ThemedSelect
            aria-label="Template key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className={inputClasses}
            isClearable
          >
            <option value="">All templates</option>
            {keys.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div className="sm:w-44">
          <ThemedSelect
            aria-label="Channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className={inputClasses}
            isClearable
          >
            <option value="">All channels</option>
            {['whatsapp', 'telegram', 'sms', 'in_app', 'fake'].map((c) => (
              <option key={c} value={c}>
                {channelName(c)}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div className="sm:w-40">
          <ThemedSelect
            aria-label="Language"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className={inputClasses}
            isClearable
          >
            <option value="">Both languages</option>
            <option value="en">English</option>
            <option value="af">Afrikaans</option>
          </ThemedSelect>
        </div>
        <p className="sm:ml-auto text-xs text-slate-500">{visible.length} shown</p>
      </div>
      {visible.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-500">No templates match.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {visible.map((t) => (
            <li key={rowKey(t)} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-semibold text-slate-900">{t.key}</span>
                <span className="text-xs text-slate-600">
                  {channelName(t.channel)} · {langName(t.language)}
                </span>
                <span
                  className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${t.tenant_id != null ? 'bg-primary-50 text-primary-800' : 'bg-slate-100 text-slate-600'}`}
                >
                  {t.tenant_id != null ? 'Our override' : 'Platform default'}
                </span>
                {t.channel === 'whatsapp' && (
                  <span
                    className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${PROVIDER_META[t.provider_status] || 'bg-slate-100 text-slate-600'}`}
                  >
                    Meta: {t.provider_status} · {t.category}
                  </span>
                )}
                {t.active === false && (
                  <span className="text-[11px] font-semibold text-slate-500">inactive</span>
                )}
                <span className="text-[11px] text-slate-400">v{t.version ?? 1}</span>
                {canManage && editing !== rowKey(t) && (
                  <button
                    type="button"
                    onClick={() => setEditing(rowKey(t))}
                    className="ml-auto text-sm font-medium text-primary-700 hover:underline"
                  >
                    {t.tenant_id != null ? 'Edit' : 'Override'}
                  </button>
                )}
              </div>
              {editing === rowKey(t) ? (
                <div className="mt-3">
                  <TemplateEditor
                    template={t}
                    onCancel={() => setEditing(null)}
                    onSaved={() => {
                      setEditing(null);
                      onChanged();
                    }}
                  />
                </div>
              ) : (
                <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap line-clamp-3 font-mono text-xs">
                  {t.body}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
